import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { shallow } from 'zustand/shallow';

import useDiscoverStore, { DiscoverActions } from '../../../zustand-stores/DiscoverStore';
import useActionStates from '../../../hooks/useActionStates';
import { GET_NON_DAILY_PROGRAMS } from '../graphql';
import type { DiscoverStore } from '../../../zustand-stores/DiscoverStore/initialDiscoverStore';
import type { DiscoverInput } from './types';

type Filters = Pick<
  DiscoverInput['input'],
  | 'timeLow'
  | 'timeHigh'
  | 'programTagIds'
  | 'minimumMen'
  | 'minimumWomen'
  | 'minimumNonBinary'
  | 'genders'
  | 'cityName'
  | 'venueIds'
  | 'sportNames'
  | 'daysOfWeek'
  | 'skillLevels'
  | 'programTypes'
  | 'neighborhoodIds'
  | 'premierProgramsOnly'
  | 'voloPassExclusiveOnly'
> &
  Pick<DiscoverStore, 'isDaily'>;

const useNonDailyResults = (sharedFilters: Filters) => {
  const { setError } = useActionStates();

  const {
    timeLow,
    timeHigh,
    isDaily = false,
    programTagIds,
    genders,
    cityName,
    venueIds,
    sportNames,
    daysOfWeek,
    skillLevels,
    programTypes,
    neighborhoodIds,
    premierProgramsOnly,
    voloPassExclusiveOnly,
  } = sharedFilters;

  const { numPerPage, currentPage } = useDiscoverStore(
    state => ({
      numPerPage: state.numPerPage,
      currentPage: state.currentPage,
    }),
    shallow
  );

  const input = {
    programTypes,
    cityName,
    daysOfWeek,
    pagination: {
      pageNum: currentPage,
      numPerPage,
    },
    sportNames,
    venueIds,
    neighborhoodIds,
    skillLevels,
    programTagIds,
    genders,
    timeLow,
    timeHigh,
    premierProgramsOnly,
    voloPassExclusiveOnly,
  };

  const nonDailySkip = !cityName || isDaily;

  const { data, loading, error, refetch, fetchMore } = useQuery(GET_NON_DAILY_PROGRAMS, {
    skip: nonDailySkip,
    variables: {
      input,
    },
  });

  const programs = data?.discoverPrograms?.leagues || [];
  const count = data?.discoverPrograms?.count;

  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async () => {
    setRefreshing(true);
    DiscoverActions.updateCurrentPage(1);
    try {
      await refetch({
        input: {
          ...input,
          pagination: {
            pageNum: 1,
            numPerPage,
          },
        },
      });
    } catch (e) {
      setError(`[useNonDailyResults]Err - handleRefresh -> ${e}`);
    } finally {
      setRefreshing(false);
    }
  };

  const [fetchMoreLoading, setFetchMoreLoading] = useState(false);
  const fetchPageResults = async (page: number) => {
    if (isDaily) return;
    setFetchMoreLoading(true);
    DiscoverActions.updateCurrentPage(page);
    try {
      await fetchMore({
        variables: {
          input: {
            ...input,
            pagination: {
              pageNum: page,
              numPerPage,
            },
          },
        },
      });
    } catch (e) {
      setError(`[useNonDailyResults]Err - fetchPageResults -> ${e}`);
    } finally {
      setFetchMoreLoading(false);
    }
  };

  return {
    error,
    loading,
    data: { programs, count },
    refresh: { handleRefresh, refreshing },
    fetchMore: { fetchPageResults, fetchMoreLoading },
  };
};

export default useNonDailyResults;
